import { Vector3 } from 'three'
import { useMeasures } from '../../../../../hooks/state/useMeasures'
import { useRoof } from '../../../../../hooks/state/useRoof'
import { CachedMeshPhysicalMaterial } from '../../../../material/cachedMeshPhysicalMaterial'

interface Props {
  roofBarSize: {
    length: number
    width: number
    deg: number
    barDepth: number
    rad: number
    offset: number
  }
}

export const RoofBeams = ({ roofBarSize }: Props): JSX.Element => {
  const { userWidth, verandaTyp } = useMeasures()

  const { color, roofGlassDepth, roofLogAmount, slatAngle } = useRoof()
  const beamRotation = slatAngle

  const roofBeamLength = beamRotation
    ? roofBarSize.length
    : userWidth - verandaTyp.log.width

  const spaceBetweenBeams = beamRotation
    ? userWidth - verandaTyp.log.width - verandaTyp.roofBar.depth * 2
    : roofBeamLength

  const getPositionOuter = (index: number) => {
    return new Vector3(
      beamRotation ? -roofBarSize.length / 2 : 0,
      -(roofGlassDepth * 2),
      beamRotation
        ? -(userWidth - verandaTyp.log.width - verandaTyp.roofBar.depth) / 2 +
          (userWidth - verandaTyp.log.width - verandaTyp.roofBar.depth) * index
        : (roofBarSize.length - verandaTyp.roofBar.depth) * index +
          verandaTyp.roofBar.depth / 2
    )
  }

  const getPositionInner = (index: number) => {
    return new Vector3(
      beamRotation ? -roofBeamLength / 2 : 0,
      -(roofGlassDepth * 2),
      beamRotation
        ? (spaceBetweenBeams / (roofLogAmount + 1)) * index
        : (roofBarSize.length / (roofLogAmount + 1)) * index +
          roofBarSize.length / (roofLogAmount + 1)
    )
  }

  return (
    <group rotation={beamRotation ? [0, Math.PI / 2, 0] : [0, 0, 0]}>
      <group
        position={[
          beamRotation ? 0 : 0,
          0,
          beamRotation
            ? -spaceBetweenBeams / 2 + spaceBetweenBeams / (roofLogAmount + 1)
            : 0,
        ]}
      >
        {[...Array(roofLogAmount)].map((_, index) => {
          return (
            <mesh
              key={`roofBeamsInner${index}`}
              position={getPositionInner(index)}
            >
              <boxGeometry
                args={[
                  roofBeamLength,
                  verandaTyp.roofBar.width,
                  verandaTyp.roofBar.depth,
                ]}
              />
              <CachedMeshPhysicalMaterial color={color} />
            </mesh>
          )
        })}
      </group>
      {[...Array(2)].map((_, index) => {
        return (
          <mesh
            key={`roofBeamsOuter${index}`}
            position={getPositionOuter(index)}
          >
            <boxGeometry
              args={[
                roofBeamLength,
                verandaTyp.roofBar.width,
                verandaTyp.roofBar.depth,
              ]}
            />
            <CachedMeshPhysicalMaterial color={color} />
          </mesh>
        )
      })}
    </group>
  )
}
